/**
 * AreaCreateSheet — bottom sheet to create a named area + register its finding
 * for the current service, in a single action (for the tech app).
 */
import React, { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Save, Square } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const RISK_OPTS = [
    { v: "bajo",  l: "Bajo" },
    { v: "medio", l: "Medio" },
    { v: "alto",  l: "Alto" },
];
const PEST_OPTS = [
    { v: "raton",               l: "Ratón" },
    { v: "rata",                l: "Rata" },
    { v: "insectos_rastreros",  l: "Insectos rastreros" },
    { v: "insectos_voladores",  l: "Insectos voladores" },
];

export default function AreaCreateSheet({ open, rect, floorPlanId, serviceId, onClose, onSaved }) {
    const [form, setForm] = useState({ name: "", risk_level: "bajo", pest_type: "raton", notes: "" });
    const [busy, setBusy] = useState(false);

    React.useEffect(() => {
        if (open) setForm({ name: "", risk_level: "bajo", pest_type: "raton", notes: "" });
    }, [open]);

    if (!rect) return null;

    const handleSave = async () => {
        if (!form.name.trim()) { toast.error("Escribe un nombre para el área"); return; }
        setBusy(true);
        try {
            // 1) Create the named area on the floor plan
            const areaRes = await api.post(`/floor-plans/${floorPlanId}/areas`, {
                name: form.name.trim(),
                x: rect.x, y: rect.y, w: rect.w, h: rect.h,
            });
            // The endpoint returns the updated plan. Find the newly added area (last one).
            const updatedPlan = areaRes.data;
            const newArea = (updatedPlan.areas || []).slice(-1)[0];
            // 2) Register the finding for this service
            if (newArea?.area_id) {
                await api.post(`/area-findings`, {
                    service_id: serviceId,
                    floor_plan_id: floorPlanId,
                    area_id: newArea.area_id,
                    risk_level: form.risk_level,
                    pest_type: form.pest_type,
                    notes: form.notes,
                });
            }
            toast.success("Área creada y hallazgo registrado");
            onSaved?.(updatedPlan);
            onClose?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error al guardar");
        } finally { setBusy(false); }
    };

    return (
        <Sheet open={open} onOpenChange={(v) => !v && onClose?.()}>
            <SheetContent side="bottom" className="rounded-t-2xl max-h-[92vh] overflow-y-auto" data-testid="area-create-sheet">
                <SheetHeader className="text-left">
                    <SheetTitle className="flex items-center gap-2">
                        <Square className="w-4 h-4 text-blue-600" />
                        Nombrar área
                    </SheetTitle>
                    <SheetDescription className="text-xs">
                        Nombra la zona dibujada y registra el hallazgo en una sola acción.
                    </SheetDescription>
                </SheetHeader>

                <div className="space-y-4 mt-4 pb-4">
                    <div>
                        <Label>Nombre del área *</Label>
                        <Input
                            autoFocus
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            placeholder="Ej. Cocina, Bodega Norte, Pasillo"
                            data-testid="area-create-name"
                        />
                    </div>
                    <div>
                        <Label>Nivel de riesgo</Label>
                        <Select value={form.risk_level} onValueChange={(v) => setForm({ ...form, risk_level: v })}>
                            <SelectTrigger data-testid="area-create-risk"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {RISK_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Tipo de plaga</Label>
                        <Select value={form.pest_type} onValueChange={(v) => setForm({ ...form, pest_type: v })}>
                            <SelectTrigger data-testid="area-create-pest"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {PEST_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Observaciones</Label>
                        <Textarea
                            rows={3}
                            value={form.notes}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            placeholder="Notas libres..."
                            data-testid="area-create-notes"
                        />
                    </div>
                </div>

                <SheetFooter className="sticky bottom-0 bg-white border-t pt-3 -mx-6 px-6 pb-4 flex gap-2">
                    <Button variant="outline" onClick={onClose} className="flex-1">Cancelar</Button>
                    <Button onClick={handleSave} disabled={busy || !form.name.trim()} className="flex-1" data-testid="area-create-save">
                        <Save className="w-4 h-4 mr-1" /> Guardar
                    </Button>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
}
